import { Component, computed, inject } from '@angular/core';

import { AuthService } from '@echospaces-web-monorepo/es-auth';
import { CardComponent, EmptyStateComponent } from '@echospaces-web-monorepo/es-shared-ui';

import { UpgradesPanelComponent } from './upgrades-panel.component';

const SUPER_ADMIN_ROLE = 'super-admin';

/**
 * Per-user settings page — shows the calling user's identity + realm
 * roles. Personal preferences (theme, language, notifications) will
 * land here. Organization-level concerns (members, branding, billing)
 * live under `/admin` instead.
 *
 * Super-admins additionally get the schema-upgrade approval panel here —
 * approving runs the pre-upgrade backup + migration, so it's gated to the
 * `super-admin` realm role.
 */
@Component({
  selector: 'es-settings-page',
  imports: [CardComponent, EmptyStateComponent, UpgradesPanelComponent],
  template: `
    <div class="max-w-4xl mx-auto">
      <div class="mb-6">
        <h1 class="text-2xl font-bold font-exo">Settings</h1>
        <p class="text-sm text-[var(--text-secondary)]">Your account and personal preferences.</p>
      </div>

      @if (auth.user(); as user) {
        <es-card>
          <h2 class="text-base font-semibold mb-4">Account</h2>
          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-[var(--text-secondary)]">Name</span>
              <p class="font-medium">{{ displayName(user) }}</p>
              <p class="text-xs text-[var(--text-secondary)]">{{ user.email }}</p>
            </div>
            <div>
              <span class="text-[var(--text-secondary)]">Organization ID</span>
              <p class="font-mono text-xs break-all">{{ user.organizationId ?? '—' }}</p>
            </div>
            <div>
              <span class="text-[var(--text-secondary)]">Realm roles</span>
              <p class="font-mono text-xs">{{ user.roles.length > 0 ? user.roles.join(', ') : '—' }}</p>
            </div>
          </div>
        </es-card>
      } @else {
        <es-empty-state
          title="Not signed in"
          description="Sign in to see your account details."
          icon="🔒"
        ></es-empty-state>
      }

      @if (isSuperAdmin()) {
        <section class="mt-8">
          <h2 class="text-base font-semibold mb-1">Schema updates</h2>
          <p class="text-sm text-[var(--text-secondary)] mb-4">
            Approve pending schema upgrades for your organization. Approving runs a
            pre-upgrade backup, then the migration.
          </p>
          <es-upgrades-panel></es-upgrades-panel>
        </section>
      }
    </div>
  `,
})
export class SettingsPageComponent {
  readonly auth = inject(AuthService);

  /** Only super-admins may approve schema upgrades — gate the panel on the role. */
  readonly isSuperAdmin = computed(() => this.auth.user()?.roles.includes(SUPER_ADMIN_ROLE) ?? false);

  displayName(user: { firstName?: string; lastName?: string; username: string }): string {
    const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return full || user.username;
  }
}
